import { Component, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import {
	PaymentReceiptService,
	PaymentReceiptStatusRow,
} from '@lmc-app/core/services/payment-receipt.service';

@Component({
	selector: 'app-create-payment-receipt',
	templateUrl: './create-payment-receipt.component.html',
	styleUrls: ['./create-payment-receipt.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatCardModule,
		MatCheckboxModule,
		MatChipsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatDatepickerModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		MatSnackBarModule,
		MatTableModule,
		MatTooltipModule,
	],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
	],
})
export class CreatePaymentReceiptComponent implements OnDestroy {
	// Setter (not a plain @ViewChild) so the paginator binds whenever the table
	// is rendered inside *ngIf, including the first load.
	@ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
		if (paginator) {
			this.dataSource.paginator = paginator;
		}
	}

	private readonly _onDestroy = new Subject<void>();

	readonly FormatUtil = FormatUtil;

	readonly displayedColumns: string[] = [
		'select',
		'status',
		'loanNumber',
		'customerName',
		'recipientEmail',
		'datePaid',
		'paymentReferenceNo',
		'paymentAmount',
	];

	readonly rangeForm = new FormGroup({
		from: new FormControl<unknown>(null, Validators.required),
		to: new FormControl<unknown>(null, Validators.required),
	});

	readonly dataSource = new MatTableDataSource<PaymentReceiptStatusRow>([]);

	counts = { MISSING: 0, QUEUED: 0, NO_EMAIL: 0 };

	/** Selected paymentHistoryRowIds (only MISSING rows are selectable). */
	readonly selected = new Set<number>();

	loading = false;
	loaded = false;
	generating = false;

	constructor(
		private paymentReceiptService: PaymentReceiptService,
		private snackBar: MatSnackBar,
	) {}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	onSubmit(): void {
		if (this.rangeForm.invalid) {
			this.rangeForm.markAllAsTouched();
			this.snackBar.open('Select a from and to date.', 'Close', { duration: 4000 });
			return;
		}

		const from = this.toDateString(this.rangeForm.value.from);
		const to = this.toDateString(this.rangeForm.value.to);
		if (dayjs(to).isBefore(dayjs(from))) {
			this.snackBar.open('"To" date must be on or after "From" date.', 'Close', { duration: 4000 });
			return;
		}

		this.loading = true;
		this.selected.clear();

		this.paymentReceiptService
			.getStatus(from, to)
			.pipe(takeUntil(this._onDestroy))
			.subscribe({
				next: (response) => {
					this.counts = response.counts;
					this.dataSource.data = response.rows;
					this.loaded = true;
					this.loading = false;
				},
				error: (error) => {
					this.loading = false;
					this.snackBar.open(
						error?.error?.message || 'Failed to load payment receipt status.',
						'Close',
						{ duration: 5000 },
					);
				},
			});
	}

	onClear(): void {
		this.rangeForm.reset({ from: null, to: null });
		this.clearResults();
	}

	private clearResults(): void {
		this.dataSource.data = [];
		this.counts = { MISSING: 0, QUEUED: 0, NO_EMAIL: 0 };
		this.selected.clear();
		this.loaded = false;
	}

	isSelectable(row: PaymentReceiptStatusRow): boolean {
		return row.status === 'MISSING';
	}

	toggleRow(row: PaymentReceiptStatusRow): void {
		if (!this.isSelectable(row)) {
			return;
		}
		if (this.selected.has(row.paymentHistoryRowId)) {
			this.selected.delete(row.paymentHistoryRowId);
		} else {
			this.selected.add(row.paymentHistoryRowId);
		}
	}

	private get missingRows(): PaymentReceiptStatusRow[] {
		return this.dataSource.data.filter((row) => row.status === 'MISSING');
	}

	get missingCount(): number {
		return this.missingRows.length;
	}

	isAllMissingSelected(): boolean {
		const missing = this.missingRows;
		return missing.length > 0 && missing.every((row) => this.selected.has(row.paymentHistoryRowId));
	}

	isSomeMissingSelected(): boolean {
		return this.selected.size > 0 && !this.isAllMissingSelected();
	}

	toggleAllMissing(): void {
		if (this.isAllMissingSelected()) {
			this.selected.clear();
		} else {
			this.missingRows.forEach((row) => this.selected.add(row.paymentHistoryRowId));
		}
	}

	onGenerate(): void {
		if (this.selected.size === 0 || this.generating) {
			return;
		}

		const rowIds = Array.from(this.selected);
		this.generating = true;

		this.paymentReceiptService
			.generate(rowIds)
			.pipe(takeUntil(this._onDestroy))
			.subscribe({
				next: (response) => {
					this.generating = false;
					const queued = response.summary['QUEUED'] || 0;
					const failed = rowIds.length - queued;
					this.snackBar.open(
						`Queued ${queued} receipt(s)${failed > 0 ? `, ${failed} not queued` : ''}.`,
						'Close',
						{ duration: 6000 },
					);
					// Clear the list so the same rows can't be submitted again.
					// Re-run Submit to see fresh status.
					this.clearResults();
				},
				error: (error) => {
					this.generating = false;
					this.snackBar.open(
						error?.error?.message || 'Failed to generate payment receipts.',
						'Close',
						{ duration: 5000 },
					);
				},
			});
	}

	private toDateString(value: unknown): string {
		return value ? dayjs(value as dayjs.ConfigType).format('YYYY-MM-DD') : '';
	}
}
